import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PhoneOff, PhoneCall, CalendarCheck, MessageSquare, Languages, Clock,
  ArrowUpRight, Zap, Users, ShieldCheck, Sparkles, TrendingUp, Headphones,
  UserCog, Megaphone, CalendarClock, LifeBuoy, ArrowRight, Check, Mail, Loader2, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import DemoWidget from "@/components/DemoWidget";
import { PricingCards } from "@/components/Pricing";
import { TRIAL_DAYS } from "@/components/pricingData";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const industries = [
  "Barbers", "Hair Salons", "Dentists", "Estate Agents", "Plumbers",
  "Car Garages", "Restaurants", "Takeaways", "Hotels", "Solicitors",
  "Accountants", "Cleaning Co.", "Electricians", "Beauty Clinics",
];

const losses = [
  { icon: CalendarCheck, label: "Lost appointments" },
  { icon: TrendingUp, label: "Lost sales & orders" },
  { icon: MessageSquare, label: "Missed quotations" },
  { icon: Users, label: "Frustrated customers" },
];

const capabilities = [
  { icon: PhoneCall, title: "Answers every call", desc: "Picks up instantly, 24/7, so no enquiry ever goes unanswered — day or night." },
  { icon: CalendarCheck, title: "Books appointments", desc: "Holds natural conversations, checks details and schedules bookings for you." },
  { icon: MessageSquare, title: "Takes messages", desc: "Captures caller details and sends you a clean summary after every call." },
  { icon: Languages, title: "Speaks their language", desc: "Continues the conversation in the caller's preferred language, where enabled." },
  { icon: Zap, title: "Transfers urgent calls", desc: "Recognises priority situations and routes them straight to your team." },
  { icon: ShieldCheck, title: "Consistent & professional", desc: "Every caller gets the same warm, on-brand experience — never rushed." },
];

const workforce = [
  { icon: TrendingUp, title: "AI Sales Assistant", tag: "Coming soon" },
  { icon: LifeBuoy, title: "AI Customer Support", tag: "Coming soon" },
  { icon: CalendarClock, title: "AI Scheduling Assistant", tag: "Coming soon" },
  { icon: UserCog, title: "AI HR Assistant", tag: "Coming soon" },
  { icon: Megaphone, title: "AI Marketing Assistant", tag: "Coming soon" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = user && user.id;
  const onSelectPlan = (planId) => navigate(authed ? `/onboarding?plan=${planId}` : `/register?plan=${planId}`);

  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const setC = (k, v) => setContact({ ...contact, [k]: v });
  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", contact);
      toast.success("Thanks! Your message has been sent — we'll be in touch shortly.");
      setContact({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Sorry, something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-1.5" data-testid="brand-logo">
            <img src="/operamind-mark.png" alt="OperaMind" className="h-10 w-10 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">Opera<span className="text-blue-600">Mind</span></span>
              <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-900">Intelligence. Orchestrated.</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#solution" className="hover:text-slate-900 transition-colors">Solution</a>
            <a href="#capabilities" className="hover:text-slate-900 transition-colors">Capabilities</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
            <a href="#workforce" className="hover:text-slate-900 transition-colors">AI Workforce</a>
            <button onClick={() => navigate("/web")} data-testid="nav-web-link" className="hover:text-slate-900 transition-colors">Web Design</button>
            <a href="#contact" className="hover:text-slate-900 transition-colors">Contact</a>
          </nav>
          <div className="flex items-center gap-2">
            {!authed && (
              <Button data-testid="nav-signin-btn" variant="ghost" onClick={() => navigate("/login")} className="text-slate-600">Sign in</Button>
            )}
            <Button data-testid="nav-dashboard-btn" onClick={() => navigate(authed ? "/dashboard" : "/pricing")} className="btn-sheen bg-slate-900 hover:bg-slate-800 rounded-md gap-1.5">
              {authed ? "Open Dashboard" : "Get started"} <ArrowUpRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(/operamind-cover.png)",
            backgroundSize: "cover",
            backgroundPosition: "center top",
          }}
        />
        {/* dark scrim so the headline stays readable over the photo */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-slate-950/40" />
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-slate-50" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-28 lg:min-h-[600px] flex items-center">
          <div className="animate-fade-up max-w-xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-white/90">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> AI Receptionist
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02] text-white [text-shadow:0_2px_20px_rgba(0,0,0,0.35)]">
              Never miss another<br />
              <span className="text-blue-400">customer call</span> again.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed max-w-lg">
              When your team is busy, OperaMind's AI receptionist answers every call
              professionally — booking appointments, taking messages and answering
              questions, in multiple languages, around the clock.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button data-testid="hero-try-demo" onClick={() => document.getElementById("solution")?.scrollIntoView({ behavior: "smooth" })} className="btn-sheen bg-blue-600 hover:bg-blue-700 rounded-md gap-2 h-11 px-6 text-base">
                <PhoneCall className="h-4 w-4" /> Try the live demo
              </Button>
              <Button data-testid="hero-open-dashboard" onClick={() => navigate("/pricing")} variant="outline" className="rounded-md h-11 px-6 text-base border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white">
                View pricing
              </Button>
              <Button data-testid="hero-web-design" onClick={() => navigate("/web")} variant="outline" className="rounded-md gap-2 h-11 px-6 text-base border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white">
                <Globe className="h-4 w-4" /> Web design
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-200">
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-blue-400" /> Answers 24/7</span>
              <span className="flex items-center gap-2"><Languages className="h-4 w-4 text-blue-400" /> Multilingual</span>
              <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-blue-400" /> Instant setup</span>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Olivia */}
      <section id="demo" className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-3xl mx-auto reveal">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Live AI Demonstration
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
            🎙️ Experience Olivia — OperaMind's AI Receptionist
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Try a live demonstration of OperaMind's AI Receptionist. Pretend you're calling any business
            and experience how OperaMind answers calls naturally using AI — Olivia adapts to whatever
            business you choose.
          </p>
        </div>

        <div className="mt-8 max-w-3xl mx-auto rounded-xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900 text-center leading-relaxed">
          This is a live demonstration of OperaMind's AI Receptionist. Olivia is showing how OperaMind
          works for different businesses. Simply pretend you're calling any business and begin your
          conversation naturally — start the conversation below.
        </div>

        <div id="solution" className="mt-8 max-w-2xl mx-auto animate-fade-up scroll-mt-24">
          <DemoWidget />
        </div>

        {/* business scenarios */}
        <div className="mt-8 flex flex-wrap justify-center gap-2.5">
          {[["💈","Barber Shop"],["🍕","Fast Food Shop"],["🍽️","Restaurant"],["🦷","Dentist"],["🏥","Medical Clinic"],["🏠","Estate Agent"],["🔐","Security Company"],["🚗","Garage"],["🔧","Plumber"],["⚡","Electrician"],["🏨","Hotel"],["💅","Beauty Salon"],["🛍️","Retail Store"]].map(([emoji, label]) => (
            <span key={label} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700">
              <span aria-hidden>{emoji}</span> {label}
            </span>
          ))}
        </div>

        {/* languages */}
        <div className="mt-10 max-w-2xl mx-auto text-center">
          <div className="flex flex-wrap justify-center gap-2.5">
            {[["🇬🇧","English"],["🇪🇸","Spanish"],["🇫🇷","French"],["🇸🇦","Arabic"]].map(([flag, lang]) => (
              <span key={lang} className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-medium">
                <span aria-hidden>{flag}</span> {lang}
              </span>
            ))}
          </div>
          <p className="mt-4 text-sm text-slate-500">
            Olivia can continue the conversation in any of the supported languages if you begin speaking one of them.
          </p>
          <Button
            data-testid="scroll-to-demo"
            onClick={() => document.getElementById("solution")?.scrollIntoView({ behavior: "smooth", block: "center" })}
            className="mt-6 bg-blue-600 hover:bg-blue-700 rounded-md gap-2 h-11 px-6 text-base"
          >
            <PhoneCall className="h-4 w-4" /> Try Olivia now
          </Button>
        </div>
      </section>

      {/* Problem band */}
      <section className="relative bg-slate-900 text-white overflow-hidden">
        <div className="glow-blob glow-indigo glow-float h-96 w-96 -top-20 -left-20 opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center reveal">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-red-400">
                <PhoneOff className="h-4 w-4" /> The missed-call problem
              </span>
              <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                A single missed call can cost you a customer worth hundreds.
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-lg">
                Staff are serving customers, on jobs or on the road. The phone rings,
                nobody answers — and the caller simply rings the next business instead.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {losses.map((l) => (
                <div key={l.label} className="rounded-xl border border-white/10 bg-white/5 p-5">
                  <l.icon className="h-6 w-6 text-red-400" />
                  <p className="mt-3 font-semibold text-sm">{l.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="max-w-7xl mx-auto px-6 py-16 text-center reveal">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Built for busy businesses</p>
        <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight">One problem. Every industry.</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {industries.map((i) => (
            <span key={i} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-blue-300 hover:text-blue-600 transition-colors">
              {i}
            </span>
          ))}
        </div>
      </section>

      {/* Capabilities bento */}
      <section id="capabilities" className="relative max-w-7xl mx-auto px-6 py-16 overflow-hidden">
        <div className="glow-blob glow-blue glow-float h-72 w-72 -top-8 -left-10" />
        <div className="glow-blob glow-indigo glow-float h-80 w-80 top-20 right-0" style={{ animationDelay: "3s" }} />
        <div className="relative max-w-2xl reveal">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">What it does</p>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            An intelligent receptionist that never clocks off.
          </h2>
        </div>
        <div className="relative mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {capabilities.map((c, i) => (
            <div key={c.title} data-delay={(i % 3) + 1} className="reveal card-fancy group p-6">
              <div className="h-11 w-11 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <c.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-heading font-bold text-lg tracking-tight">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Multilingual highlight */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="reveal rounded-2xl bg-blue-600 text-white p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center overflow-hidden relative">
          <div>
            <Languages className="h-8 w-8 text-blue-200" />
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-bold tracking-tight">Serve every customer, in their language.</h2>
            <p className="mt-3 text-blue-100 leading-relaxed max-w-lg">
              Background noise, strong accents and different first languages make calls
              hard. OperaMind can continue the conversation in the caller's preferred
              language — for the languages you choose to enable.
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5 lg:justify-end">
            {["English", "Español", "Polski", "Français", "العربية", "中文", "हिन्दी", "Português"].map((l) => (
              <span key={l} className="rounded-full bg-white/15 border border-white/20 px-4 py-2 text-sm font-medium">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative max-w-7xl mx-auto px-6 py-16 overflow-hidden">
        <div className="glow-blob glow-sky glow-float h-80 w-80 top-0 left-1/2 -translate-x-1/2" />
        <div className="relative text-center max-w-2xl mx-auto mb-10 reveal">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            <Sparkles className="h-3.5 w-3.5 text-blue-600" /> Pricing
          </span>
          <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Plans that pay for themselves.</h2>
          <p className="mt-3 text-slate-600">Every plan includes a {TRIAL_DAYS}-day free trial. No card required to start.</p>
        </div>
        <div className="relative"><PricingCards onSelect={onSelectPlan} currentPlan={null} /></div>
        <div className="relative mt-6 text-center">
          <button data-testid="see-full-pricing" onClick={() => navigate("/pricing")} className="text-sm font-medium text-blue-600 hover:underline">See full pricing & setup options →</button>
        </div>
      </section>

      {/* AI Workforce */}
      <section id="workforce" className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto reveal">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            <Sparkles className="h-3.5 w-3.5 text-blue-600" /> The long-term vision
          </span>
          <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">A complete AI Workforce, one dashboard.</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            OperaMind starts with the AI Receptionist and grows into a team of AI
            employees you deploy and manage from a single place.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="reveal rounded-xl border-2 border-blue-600 bg-white p-6 relative">
            <span className="absolute top-4 right-4 rounded-full bg-emerald-500 text-white text-xs font-bold px-2.5 py-0.5">Live now</span>
            <div className="h-11 w-11 rounded-lg bg-blue-600 flex items-center justify-center text-white"><Headphones className="h-5 w-5" /></div>
            <h3 className="mt-4 font-heading font-bold text-lg tracking-tight">AI Receptionist</h3>
            <p className="mt-2 text-sm text-slate-600">Answering your calls today.</p>
          </div>
          {workforce.map((w, i) => (
            <div key={w.title} data-delay={(i % 4) + 1} className="reveal card-fancy p-6 relative">
              <span className="absolute top-4 right-4 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold px-2.5 py-0.5">{w.tag}</span>
              <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400"><w.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-heading font-bold text-lg tracking-tight text-slate-700">{w.title}</h3>
              <p className="mt-2 text-sm text-slate-500">Planned for the roadmap.</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-7xl mx-auto px-6 py-16">
        <div className="reveal rounded-2xl border border-slate-200 bg-white overflow-hidden grid lg:grid-cols-2">
          <div className="p-8 sm:p-12 bg-slate-900 text-white">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
              <Mail className="h-4 w-4" /> Get in touch
            </span>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-bold tracking-tight">Talk to the OperaMind team.</h2>
            <p className="mt-3 text-slate-300 leading-relaxed max-w-md">
              Questions about plans, setup or whether OperaMind fits your business? Send us a
              message and we'll get back to you — usually within one working day.
            </p>
            <div className="mt-8 space-y-3 text-sm text-slate-300">
              <a href="mailto:admin@operamind.uk" className="flex items-center gap-3 hover:text-white transition-colors">
                <span className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><Mail className="h-4 w-4" /></span>
                admin@operamind.uk
              </a>
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><Clock className="h-4 w-4" /></span>
                Mon–Fri, 9:00–18:00
              </div>
            </div>
          </div>
          <form data-testid="contact-form" onSubmit={submitContact} className="p-8 sm:p-12 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Name</Label>
                <Input data-testid="contact-name" required value={contact.name} onChange={(e) => setC("name", e.target.value)} className="mt-1" placeholder="Your name" />
              </div>
              <div>
                <Label className="text-xs">Phone (optional)</Label>
                <Input data-testid="contact-phone" value={contact.phone} onChange={(e) => setC("phone", e.target.value)} className="mt-1" placeholder="Your number" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input data-testid="contact-email" type="email" required value={contact.email} onChange={(e) => setC("email", e.target.value)} className="mt-1" placeholder="you@business.com" />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea data-testid="contact-message" required rows={4} value={contact.message} onChange={(e) => setC("message", e.target.value)} className="mt-1" placeholder="How can we help?" />
            </div>
            <Button data-testid="contact-submit" type="submit" disabled={sending} className="w-full bg-blue-600 hover:bg-blue-700 gap-2 h-11 text-base">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send message <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="reveal relative overflow-hidden rounded-2xl bg-slate-900 text-white p-10 sm:p-14 text-center">
          <div className="glow-blob glow-blue glow-float h-72 w-72 -top-16 -right-10 opacity-40" />
          <h2 className="relative font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Recover the calls you're missing today.</h2>
          <p className="relative mt-3 text-slate-300 max-w-xl mx-auto">Configure your AI receptionist in minutes and see every call captured in your dashboard.</p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-3">
            <Button data-testid="cta-dashboard-btn" onClick={() => navigate(authed ? "/dashboard" : "/pricing")} className="btn-sheen bg-blue-600 hover:bg-blue-700 rounded-md h-11 px-6 text-base gap-2">
              {authed ? "Open your dashboard" : "Start your free trial"} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative mt-8 flex flex-wrap justify-center gap-6 text-sm text-slate-400">
            {["No missed calls", "Multilingual support", "Call summaries & bookings"].map((t) => (
              <span key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-1.5">
            <img src="/operamind-mark.png" alt="OperaMind" className="h-7 w-7 object-contain" />
            <span className="font-heading font-bold text-slate-900">Opera<span className="text-blue-600">Mind</span></span>
          </div>
          <p>© {new Date().getFullYear()} OperaMind — AI Workforce Platform.</p>
          <div className="flex items-center gap-5" data-testid="footer-legal-links">
            <button onClick={() => navigate("/web")} data-testid="footer-web-link" className="hover:text-slate-900 transition-colors">Web Design</button>
            <a href="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-slate-900 transition-colors">Terms of Service</a>
            <a href="mailto:admin@operamind.uk" className="hover:text-slate-900 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
